'use client'

import React, { ReactElement, useEffect, useState } from 'react'

import { useAtomValue } from 'jotai'
import { usePathname } from 'next/navigation'

import type { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'
import { Button } from '@dfds-pax/shared-components/client'

import { useBasket } from '@/providers/basket-context'
import { addonsBusyAtom, departuresBusyAtom, petsBusyAtom } from '@/states/loadingAtoms'
import { selectedPetsAtom } from '@/states/selectionAtom'

import { useTranslation } from '@/hooks/useDebugTranslations'

const getContinueButtonText = (basket: BffInterfacesBasket.Basket | null, showNumberOfPets: number, step: string): string => {
  const hasTwoLegs = basket?.departures && basket.departures.length > 1
  if (step && ['DEPARTURES', 'YOURBOOKING'].includes(step)) {
    if (hasTwoLegs) {
      if (!basket.departures[0].departureDateTimeForBasket) return 'BUTTON-CONTINUE-OUTBOUND'
      else if (!basket.departures[1].departureDateTimeForBasket) return 'BUTTON-CONTINUE-RETURN'
      else if (showNumberOfPets > 0) return 'BUTTON-CONTINUE-PETS'
      else return 'BUTTON-CONTINUE-EXTRAS'
    } else {
      if (showNumberOfPets > 0) return 'BUTTON-CONTINUE-PETS'
      else return 'BUTTON-CONTINUE-EXTRAS'
    }
  } else if (step === 'EXTRAS') return 'BUTTON-CONTINUE-PASSENGERS'
  else if (step === 'PETS') return 'BUTTON-CONTINUE-EXTRAS'
  else if (step === 'PASSENGERS') return 'BUTTON-CONTINUE-CHECKOUT'
  else if (step === 'CHECKOUT') return 'BUTTON-CONTINUE-REVIEW'
  else if (step === 'REVIEW') return 'GO-TO-SECURE-PAYMENT'
  return 'Unknown Step'
}

interface Props {
  form?: string
  type: 'Desktop' | 'Mobile'
}

const ContinueHandler: React.FC<Props> = ({ form, type }): ReactElement => {
  const { t } = useTranslation()

  const { showNumberOfPets } = useAtomValue(selectedPetsAtom)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const addonsBusy = useAtomValue(addonsBusyAtom).busy
  const departuresBusy = useAtomValue(departuresBusyAtom)
  const petsBusy = useAtomValue(petsBusyAtom).busy

  const { basket, basketLoading } = useBasket()
  const pathname = usePathname()
  const step = pathname.split('/').at(-1)?.toUpperCase() ?? ''

  // Block continuing while a departure/pet/addon mutation is still being committed to the basket
  const isBasketUpdating = basketLoading || addonsBusy || departuresBusy || petsBusy
  const handleFormSubmit = (formId: string) => {
    const form = document.getElementById(formId) as HTMLFormElement | null
    if (form) {
      if (!form.checkValidity()) {
        // Trigger validation display for invalid fields
        form.reportValidity()
        setIsSubmitting(false)
        return
      }

      try {
        form.requestSubmit()
      } catch {
        setIsSubmitting(false)
      }
    } else {
      setIsSubmitting(false)
    }
  }

  const handleContinue = async (): Promise<void> => {
    setIsSubmitting(true)
    if (['DEPARTURES', 'YOURBOOKING'].includes(step)) handleFormSubmit('yourbookingForm')
    else if (step === 'EXTRAS') handleFormSubmit('extrasForm')
    else if (step === 'PETS') handleFormSubmit('petsForm')
    else if (step === 'PASSENGERS') handleFormSubmit('passengerForm')
    else if (step === 'CHECKOUT') handleFormSubmit('checkoutForm')
    else if (step === 'REVIEW') handleFormSubmit('paymentForm')
  }

  const continueButtonText = getContinueButtonText(basket, showNumberOfPets, step)

  useEffect(() => {
    // Generic reset for any step whose submit is aborted while keeping the user on the page
    // (a backend error surfaced in a modal, a dismissed confirmation modal, etc.), so the
    // Continue spinner never gets stuck.
    const continueAbortedHandler = () => setIsSubmitting(false)

    const formFieldErrorHandler = (event: Event) => {
      const customEvent = event as CustomEvent<{ formId: string }>
      // Only set isSubmitting to false if the error is for a form this handler cares about.
      // i.e. only passengerForm and checkoutForm have user-editable fields that can be invalid; the other forms are modals or have no input fields, so they cannot have field errors.
      if (customEvent.detail?.formId === 'passengerForm' || customEvent.detail?.formId === 'checkoutForm') {
        setIsSubmitting(false)
      }
    }

    // Show the spinner when paymentForm is submitted through a path that bypasses ContinueHandler
    // (ReviewForm's own submit button, pressing Enter, or the retry dialog's requestSubmit).
    // Every other step sets isSubmitting via doContinueHandler, so paymentForm is the only form
    // that relies on this native submit listener.
    const handleFormSubmit = (event: Event) => {
      const form = event.target as HTMLFormElement
      if (form.id === 'paymentForm') {
        setIsSubmitting(true)
      }
    }

    window.addEventListener('continueAborted', continueAbortedHandler)
    window.addEventListener('formFieldError', formFieldErrorHandler)
    document.addEventListener('submit', handleFormSubmit)

    return () => {
      window.removeEventListener('continueAborted', continueAbortedHandler)
      window.removeEventListener('formFieldError', formFieldErrorHandler)
      document.removeEventListener('submit', handleFormSubmit)
    }
  }, [])

  return (
    <Button
      data-e2e-role="continue-button"
      disabled={isSubmitting || isBasketUpdating}
      form={form}
      fullWidth
      id={`continueButton-${type}`}
      isLoading={isSubmitting}
      disabledWhileLoading={isSubmitting}
      surface="dynamic"
      onClick={(e) => {
        e.preventDefault() // cancels the native submit
        if (!isSubmitting && !isBasketUpdating) {
          handleContinue() // runs on Enter too
        }
      }}
      size="lg"
      tabIndex={3}
      title={t(continueButtonText)}
      translate="no"
      type="submit"
      variant="primary"
    >
      {!isSubmitting ? t(continueButtonText) : null}
    </Button>
  )
}

export default ContinueHandler
